"use strict";

exports.__esModule = true;
exports.default = exports.Checkbox = void 0;

var _react = _interopRequireDefault(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _clsx = _interopRequireDefault(require("clsx"));

var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));

var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));

var _useLabel3 = _interopRequireDefault(require("../../hooks/useLabel"));

var _Icon = _interopRequireDefault(require("../Icon/Icon"));

var _excluded = ["id", "name", "checked", "label", "helper", "required", "disabled", "readOnly", "size", "color", "align", "outline", "onChange", "onBlur", "onError", "containerLabel", "displayAsButton", "inputClassName", "isIndeterminate", "className", "children"];

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

/**
 * The Checkbox component allows the user to make a binary choice, i.e. a choice between one of two possible mutually exclusive options.
 */
var Checkbox = function Checkbox(props) {
  var controlledId = props.id,
      name = props.name,
      controlledChecked = props.checked,
      initialLabel = props.label,
      helper = props.helper,
      required = props.required,
      disabled = props.disabled,
      readOnly = props.readOnly,
      size = props.size,
      color = props.color,
      align = props.align,
      outline = props.outline,
      onChange = props.onChange,
      onBlur = props.onBlur,
      onError = props.onError,
      initialContainerLabel = props.containerLabel,
      displayAsButton = props.displayAsButton,
      inputClassName = props.inputClassName,
      isIndeterminate = props.isIndeterminate,
      className = props.className,
      children = props.children,
      attributes = _objectWithoutPropertiesLoose(props, _excluded);

  var checkboxRef = _react.default.useRef(null);

  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;

  var _React$useState = _react.default.useState(controlledChecked || false),
      checked = _React$useState[0],
      setChecked = _React$useState[1];

  var _React$useState2 = _react.default.useState(false),
      error = _React$useState2[0],
      setError = _React$useState2[1];

  var _useFormElementValida = (0, _useFormElementValidation.default)(checked, {
    required: required
  }),
      newChecked = _useFormElementValida[0],
      errors = _useFormElementValida[1],
      methods = _useFormElementValida[2];

  var _useLabel = (0, _useLabel3.default)(initialLabel, _extends({}, props, {
    id: id,
    required: required && !initialContainerLabel
  }), false),
      label = _useLabel.label;

  var _useLabel2 = (0, _useLabel3.default)(initialContainerLabel, _extends({}, props, {
    id: id,
    required: required
  }), false),
      containerLabel = _useLabel2.label;

  _react.default.useEffect(function () {
    setChecked(controlledChecked || false);
  }, [controlledChecked]);

  _react.default.useEffect(function () {
    setChecked(newChecked);
  }, [newChecked]);

  _react.default.useEffect(function () {
    setError(errors.length > 0);
  }, [errors]);

  var handleChange = function handleChange(event) {
    methods.onChangeCheckbox(event);
    setChecked(event.target.checked);

    if (typeof onChange === 'function') {
      onChange(event.target.checked, checkboxRef.current.name, event);
    }
  };

  var handleBlur = function handleBlur(event) {
    methods.onChangeCheckbox(event);

    if (typeof onBlur === 'function') {
      onBlur(event.target.id);
    }
  };

  var classes = (0, _clsx.default)('uk-checkbox', outline && 'outline', color !== 'default' && color, size !== 'default' && size, align !== 'left' && 'reverse', disabled && 'disabled', (helper.length > 0 || error) && 'has-helper', error && 'has-error', className);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: classes
  }, containerLabel && /*#__PURE__*/_react.default.createElement("div", {
    className: "checkbox-container-label"
  }, containerLabel), /*#__PURE__*/_react.default.createElement("label", {
    htmlFor: id
  }, /*#__PURE__*/_react.default.createElement("input", _extends({
    ref: checkboxRef,
    id: id,
    name: name || id,
    type: "checkbox",
    checked: checked,
    className: (0, _clsx.default)('checkbox-input', inputClassName),
    disabled: disabled,
    readOnly: readOnly,
    required: required,
    onChange: handleChange,
    onBlur: handleBlur
  }, attributes)), /*#__PURE__*/_react.default.createElement("span", {
    className: "checkbox--fake"
  }, /*#__PURE__*/_react.default.createElement(_Icon.default, {
    name: isIndeterminate ? 'indeterminate' : 'checkmark'
  })), /*#__PURE__*/_react.default.createElement("div", {
    className: "checkbox-label"
  }, label)), (helper.length > 0 || error) && /*#__PURE__*/_react.default.createElement("small", {
    className: "helper"
  }, error ? errors : helper));
};

exports.Checkbox = Checkbox;
Checkbox.propTypes = {
  /**
   * ID of the Checkbox (used to bind the label to the Checkbox)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,

  /**
   * Name of the Checkbox (used instead of the ID when submitted)
   */
  name: _propTypes.default.string,

  /**
   * Label of the Checkbox
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),

  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,

  /**
   * Change the display of the Checkbox
   */
  displayAsButton: _propTypes.default.bool,

  /**
   * Position of the Checkbox relative to its label
   */
  align: _propTypes.default.oneOf(['left', 'right']),

  /**
   * Size of the Checkbox
   */
  size: _propTypes.default.oneOf(['tiny', 'small', 'default', 'large', 'big']),

  /**
   * Background-color of a selected Checkbox (or checkmark color if outline)
   */
  color: _propTypes.default.oneOf(['default', 'primary', 'secondary', 'success', 'alert', 'warning', 'white']),

  /**
   * Invert styling colors of the Checkbox
   */
  outline: _propTypes.default.bool,

  /**
   * Set the Checkbox as selected
   */
  checked: _propTypes.default.bool,

  /**
   * Add a high-level label
   */
  containerLabel: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),

  /**
   * Set the Checkbox as read-only
   */
  readOnly: _propTypes.default.bool,

  /**
   * Set the Checkbox as disabled
   */
  disabled: _propTypes.default.bool,

  /**
   * Form-validation: set the Checkbox as required
   */
  required: _propTypes.default.bool,

  /**
   * classes of checkbox's Input
   */
  inputClassName: _propTypes.default.string,

  /**
   * Is checkbox state indeterminate (3d state)
   */
  isIndeterminate: _propTypes.default.bool,

  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,

  /**
   * Callback function onBlur
   */
  onBlur: _propTypes.default.func,

  /**
   * Callback function onError
   */
  onError: _propTypes.default.func
};
Checkbox.defaultProps = {
  helper: '',
  displayAsButton: false,
  align: 'left',
  size: 'default',
  color: 'default',
  readOnly: false,
  disabled: false,
  required: false,
  inputClassName: '',
  isIndeterminate: false
};
var _default = Checkbox;
exports.default = _default;